import { sha256 } from "@noble/hashes/sha2.js";

export const PROTOCOL_NAME = "IPI_WALLET_APPROVAL";
export const PROTOCOL_VERSION = 2;
export const HASH_ALGORITHM = "SHA-256";
export const SIGNATURE_ALGORITHM = "secp256k1-sha256-p1363-v1";
export const REQUEST_LIFETIME_MS = 300_000;
export const MAX_REQUEST_LIFETIME_MS = 900_000;
export const RESPONSE_LIFETIME_MS = 120_000;
export const DEVELOPMENT_DEMO_RESPONSE_LIFETIME_MS = 600_000;
export const ALLOWED_CLOCK_SKEW_MS = 30_000;

const encoder = new TextEncoder();

export function utf8(value) {
  return encoder.encode(value);
}

export function bytesToHex(bytes) {
  return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}

export function hexToBytes(value, bytes, name = "hex") {
  const normalized = exactHex(value, bytes, name);
  return Uint8Array.from(normalized.match(/../g), (pair) => Number.parseInt(pair, 16));
}

export function text(value, name) {
  if (typeof value !== "string" || !value.trim()) {
    throw new TypeError(`${name} must be non-empty text`);
  }
  const normalized = value.trim().normalize("NFC");
  for (let index = 0; index < normalized.length; index += 1) {
    const code = normalized.charCodeAt(index);
    if (code >= 0xd800 && code <= 0xdbff) {
      const next = normalized.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) {
        throw new TypeError(`${name} contains invalid Unicode`);
      }
      index += 1;
    } else if (code >= 0xdc00 && code <= 0xdfff) {
      throw new TypeError(`${name} contains invalid Unicode`);
    }
  }
  return normalized;
}

function compareText(left, right) {
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

function compareAmount(left, right) {
  const a = BigInt(left);
  const b = BigInt(right);
  return a < b ? -1 : a > b ? 1 : 0;
}

export function integer(value, name, { positive = false } = {}) {
  if (!Number.isSafeInteger(value) || value < (positive ? 1 : 0)) {
    throw new TypeError(`${name} must be an exact interoperable integer`);
  }
  return value;
}

export function canonicalAmount(value, name, { positive = false } = {}) {
  if (typeof value !== "string" || !/^(?:0|[1-9][0-9]{0,77})$/u.test(value)) {
    throw new TypeError(`${name} must be a canonical aipi integer string`);
  }
  if (positive && value === "0") {
    throw new TypeError(`${name} must be positive`);
  }
  return value;
}

export function exactHex(value, bytes, name) {
  if (typeof value !== "string"
      || value.length !== bytes * 2
      || !/^[0-9a-f]+$/.test(value)) {
    throw new TypeError(`${name} must be ${bytes} bytes of lowercase hexadecimal`);
  }
  return value;
}

export function normalizeTagUid(value) {
  const normalized = text(value, "tag_uid").replace(/[ :\-]/g, "").toUpperCase();
  if (!/^(?:[0-9A-F]{2})+$/.test(normalized)) {
    throw new TypeError("tag_uid must be even-length hexadecimal");
  }
  return normalized;
}

export function sha256Hex(bytes) {
  return bytesToHex(sha256(bytes));
}

export function canonicalizeItems(sourceItems) {
  if (!Array.isArray(sourceItems) || sourceItems.length === 0) {
    throw new TypeError("items must not be empty");
  }
  return sourceItems.map((source) => {
    const quantity = integer(source.quantity, "quantity", { positive: true });
    const unitPrice = canonicalAmount(source.unit_price, "unit_price");
    const expectedLineTotal = (BigInt(unitPrice) * BigInt(quantity)).toString();
    if (canonicalAmount(source.line_total, "line_total") !== expectedLineTotal) {
      throw new TypeError("line_total must exactly equal quantity times unit_price");
    }
    return {
      product_id: text(source.product_id, "product_id"),
      tag_uid: normalizeTagUid(source.tag_uid),
      quantity,
      unit_price: unitPrice,
      line_total: expectedLineTotal,
    };
  }).sort((left, right) => (
    compareText(left.product_id, right.product_id)
    || compareText(left.tag_uid, right.tag_uid)
    || compareAmount(left.unit_price, right.unit_price)
    || left.quantity - right.quantity
  ));
}

export function itemCommitment(sourceItems) {
  const items = canonicalizeItems(sourceItems);
  const totalAmount = items.reduce(
    (total, item) => total + BigInt(item.line_total),
    0n,
  ).toString();
  canonicalAmount(totalAmount, "total_amount");
  const itemCount = items.reduce((total, item) => {
    const next = total + item.quantity;
    integer(next, "item_count", { positive: true });
    return next;
  }, 0);
  const canonicalBytes = utf8(JSON.stringify(items));
  return {
    items,
    canonical_bytes: canonicalBytes,
    item_count: itemCount,
    total_amount: totalAmount,
    item_summary_hash: sha256Hex(canonicalBytes),
  };
}

function requestBodyObject(request) {
  return {
    protocol_name: text(request.protocol_name, "protocol_name"),
    protocol_version: integer(request.protocol_version, "protocol_version", { positive: true }),
    request_id: text(request.request_id, "request_id"),
    checkout_id: text(request.checkout_id, "checkout_id"),
    terminal_id: text(request.terminal_id, "terminal_id"),
    merchant_id: text(request.merchant_id, "merchant_id"),
    created_at_ms: integer(request.created_at_ms, "created_at_ms"),
    expires_at_ms: integer(request.expires_at_ms, "expires_at_ms"),
    denomination: text(request.denomination, "denomination"),
    total_amount: canonicalAmount(request.total_amount, "total_amount"),
    item_count: integer(request.item_count, "item_count", { positive: true }),
    item_summary_hash: exactHex(request.item_summary_hash, 32, "item_summary_hash"),
    ipi_card_id: text(request.ipi_card_id, "ipi_card_id"),
    card_verified_at_ms: integer(request.card_verified_at_ms, "card_verified_at_ms"),
    card_verification_config_id: text(request.card_verification_config_id, "card_verification_config_id"),
    nonce: exactHex(request.nonce, 32, "nonce"),
    chain_id: text(request.chain_id, "chain_id"),
    response_channel: text(request.response_channel, "response_channel"),
  };
}

export function canonicalRequestBody(request) {
  return utf8(JSON.stringify(requestBodyObject(request)));
}

export function canonicalRequest(request) {
  return utf8(JSON.stringify({
    ...requestBodyObject(request),
    request_hash: exactHex(request.request_hash, 32, "request_hash"),
  }));
}

export function createRequest(fields, items) {
  const commitment = itemCommitment(items);
  if (fields.total_amount !== undefined
      && canonicalAmount(fields.total_amount, "total_amount") !== commitment.total_amount) {
    throw new TypeError("declared total_amount does not match checkout items");
  }
  const createdAt = integer(fields.created_at_ms, "created_at_ms");
  const expectedExpiry = createdAt + REQUEST_LIFETIME_MS;
  if (fields.expires_at_ms !== undefined && fields.expires_at_ms !== expectedExpiry) {
    throw new TypeError("request lifetime must be exactly five minutes");
  }
  const body = requestBodyObject({
    ...fields,
    protocol_name: PROTOCOL_NAME,
    protocol_version: PROTOCOL_VERSION,
    created_at_ms: createdAt,
    expires_at_ms: expectedExpiry,
    total_amount: commitment.total_amount,
    item_count: commitment.item_count,
    item_summary_hash: commitment.item_summary_hash,
  });
  if (body.expires_at_ms <= body.created_at_ms) {
    throw new TypeError("expires_at_ms must be after created_at_ms");
  }
  return { ...body, request_hash: sha256Hex(canonicalRequestBody(body)) };
}

function responseBodyObject(response) {
  const decision = text(response.decision, "decision");
  if (decision !== "APPROVED" && decision !== "REJECTED") {
    throw new TypeError("decision must be APPROVED or REJECTED");
  }
  const result = {
    protocol_name: text(response.protocol_name, "protocol_name"),
    protocol_version: integer(response.protocol_version, "protocol_version", { positive: true }),
    response_id: text(response.response_id, "response_id"),
    request_id: text(response.request_id, "request_id"),
    checkout_id: text(response.checkout_id, "checkout_id"),
    request_hash: exactHex(response.request_hash, 32, "request_hash"),
    wallet_public_key_id: text(response.wallet_public_key_id, "wallet_public_key_id"),
    decision,
    responded_at_ms: integer(response.responded_at_ms, "responded_at_ms"),
    expires_at_ms: integer(response.expires_at_ms, "expires_at_ms"),
    nonce: exactHex(response.nonce, 32, "nonce"),
  };
  if (decision === "REJECTED") {
    result.rejection_reason_code = text(response.rejection_reason_code, "rejection_reason_code");
  } else if (response.rejection_reason_code !== undefined && response.rejection_reason_code !== null) {
    throw new TypeError("APPROVED response cannot contain rejection_reason_code");
  }
  result.signature_algorithm = text(response.signature_algorithm, "signature_algorithm");
  return result;
}

export function createUnsignedResponse(request, fields) {
  const respondedAt = integer(fields.responded_at_ms, "responded_at_ms");
  const responseLifetime = fields.response_lifetime_ms === undefined
    ? RESPONSE_LIFETIME_MS
    : integer(fields.response_lifetime_ms, "response_lifetime_ms");
  if (responseLifetime < RESPONSE_LIFETIME_MS
      || responseLifetime > DEVELOPMENT_DEMO_RESPONSE_LIFETIME_MS) {
    throw new TypeError("response lifetime must be between 2 and 10 minutes");
  }
  const expectedExpiry = Math.min(
    respondedAt + responseLifetime,
    integer(request.expires_at_ms, "request.expires_at_ms"),
  );
  if (expectedExpiry <= respondedAt) {
    throw new TypeError("request has no remaining response lifetime");
  }
  return responseBodyObject({
    ...fields,
    protocol_name: PROTOCOL_NAME,
    protocol_version: PROTOCOL_VERSION,
    request_id: request.request_id,
    checkout_id: request.checkout_id,
    request_hash: request.request_hash,
    responded_at_ms: respondedAt,
    expires_at_ms: expectedExpiry,
    nonce: request.nonce,
    signature_algorithm: SIGNATURE_ALGORITHM,
  });
}

export function canonicalResponseBody(response) {
  return utf8(JSON.stringify(responseBodyObject(response)));
}

export function canonicalResponse(response) {
  return utf8(JSON.stringify({
    ...responseBodyObject(response),
    signature: exactHex(response.signature, 64, "signature"),
  }));
}

export function walletPublicKeyId(compressedPublicKey) {
  return `wallet-key-${sha256Hex(hexToBytes(compressedPublicKey, 33, "compressed_public_key")).slice(0, 16)}`;
}
